import { ResponseStatus, WidgetStyle } from '@sdbn/appclient';
import { Component, OnInit, ViewChild } from '@angular/core';
import { RFIDCard, RFIDCardDetails } from '../model/rfid-card.model';
import { RFIDCardService } from '../service/rfid-card.service';
import { ConfirmDialogData, ConfirmDialogComponent } from 'src/app/shared/confirm-dialog/confirm.dialog.component';
import { TranslationService } from '@sdbn/common';
import { MatLegacyDialog as MatDialog }from '@angular/material/legacy-dialog';
import { ResponseMessage } from '@sdbn/appclient';
import { UntypedFormControl as FormControl, UntypedFormGroup as FormGroup, NgForm, Validators } from '@angular/forms';
import { ContextChange } from '@sdbn/appclient';
import { MasterDetailListEntry } from '@sdbn/common';
import { AbstractWidgetComponent } from '@sdbn/appclient';

@Component({
  selector: 'app-rfid-card',
  templateUrl: './rfid-card.component.html',
  styleUrls: ['./rfid-card.component.scss']
})
export class RFIDCardComponent extends AbstractWidgetComponent implements OnInit {

  rfidCards: RFIDCard[] = [];

  selectedRFIDCard: RFIDCard;
  selectedDetails: RFIDCardDetails = new RFIDCardDetails({});
  workingCopyDetails: RFIDCardDetails = new RFIDCardDetails({});

  responseMessages: ResponseMessage[];
  isLoading = false;

  formGroup = new FormGroup({});

  get cancelEnabled() {
    return !this.isLoading && this.formGroup.dirty;
  }

  get saveEnabled() {
    return !this.isLoading && this.formGroup.dirty && this.formGroup.valid;
  }

  constructor(
    public translationService: TranslationService,
    private rfidCardService: RFIDCardService,
    private dialog: MatDialog) {
    super();

    this.formGroup.addControl('nbr', new FormControl('', [Validators.required]));
    this.formGroup.addControl('techIdentifier', new FormControl('', [Validators.required]));
    this.formGroup.addControl('description', new FormControl('', []));
    this.formGroup.addControl('active', new FormControl('', []));
    this.formGroup.addControl('validFrom', new FormControl('', []));
    this.formGroup.addControl('validUntil', new FormControl('', []));
    this.formGroup.valueChanges.subscribe(formValues => {
      this.workingCopyDetails.nbr = formValues.nbr;
      this.workingCopyDetails.techIdentifier = formValues.techIdentifier;
      this.workingCopyDetails.description = formValues.description;
      this.workingCopyDetails.active = formValues.active;
      this.workingCopyDetails.validFrom = formValues.validFrom;
      this.workingCopyDetails.validUntil = formValues.validUntil;
    });

  }

  ngOnInit() {
    this.load();
  }

  setSelected(rfidCard: RFIDCard) {

    if (this.selectedRFIDCard != null && this.selectedRFIDCard.id === rfidCard.id) {
      return;
    }

    if (this.formGroup.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc.admin.rfidcard.discard.confirm-dialog.title',
        messageKey: 'tmc.admin.rfidcard.discard.confirm-dialog.message',
        confirm: () => {
          this.setSelectedWithoutCheck(rfidCard);
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.setSelectedWithoutCheck(rfidCard);
    }

  }

  setSelectedWithoutCheck(rfidCard: RFIDCard) {
    if (rfidCard == null) {
      return;
    }
    this.setLoading(true);
    this.responseMessages = [];
    this.rfidCardService.getRFIDCardDetails({ id: rfidCard.id }).subscribe(details => {
      this.selectedRFIDCard = rfidCard;
      this.selectedDetails = details;
      this.workingCopyDetails = new RFIDCardDetails(details);
      this.resetFormGroup();
      this.setLoading(false);
    }, error => {
      this.setLoading(false);
      console.log(error);
    });
  }

  isSelected(details: RFIDCard) {
    return this.selectedDetails != null && this.selectedDetails.id === details.id;
  }

  delete(rfidCard: RFIDCard) {

    this.responseMessages = [];

    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc.admin.rfidcard.delete.confirm-dialog.title',
      messageKey: 'tmc.admin.rfidcard.delete.confirm-dialog.message',
      confirm: () => {
        this.setLoading(true);
        this.rfidCardService.deleteRFIDCard({ id: rfidCard.id }).subscribe(response => {
          if (response.status === ResponseStatus.SUCCESS) {
            this.clearAndLoad();
          } else {
            this.responseMessages = response.responseMessages;
          }
          this.setLoading(false);
        }, error => {
          console.log(error);
          this.setLoading(false);
        });
      }
    };

    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  load() {
    this.setLoading(true);
    this.responseMessages = [];
    this.rfidCardService.getRFIDCards().subscribe(rfidCards => {

      const newRFIDCard = new Array<RFIDCard>();
      for (const rfidCard of rfidCards) {
        newRFIDCard.push(new RFIDCard(rfidCard));
      }
      this.rfidCards = newRFIDCard;

      if (this.rfidCards.length > 0) {
        if (this.selectedRFIDCard == null) {
          this.setSelectedWithoutCheck(this.rfidCards[0]);
        } else {
          this.setSelectedWithoutCheck(this.rfidCards.find(s => s.id === this.selectedRFIDCard.id));
        }
      }

      this.setLoading(false);
    }, error => {
      console.error(error);
      this.setLoading(false);
    });
  }

  addNew() {
    if (this.formGroup.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc.admin.rfidcard.discard.confirm-dialog.title',
        messageKey: 'tmc.admin.rfidcard.discard.confirm-dialog.message',
        confirm: () => {
          this.clear();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.clear();
    }
  }

  cancel() {
    if (this.formGroup.dirty) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc.admin.rfidcard.discard.confirm-dialog.title',
        messageKey: 'tmc.admin.rfidcard.discard.confirm-dialog.message',
        confirm: () => {
          this.discardChanges();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.discardChanges();
    }
  }

  discardChanges() {
    this.responseMessages = [];
    if (this.workingCopyDetails.id != null) {
      this.setSelectedWithoutCheck(this.selectedRFIDCard);
    } else {
      this.workingCopyDetails = new RFIDCardDetails({});
      this.workingCopyDetails.active = true;
      this.resetFormGroup();
      this.selectedRFIDCard = null;
    }
  }

  clear() {
    this.responseMessages = [];
    this.selectedRFIDCard = null;
    this.selectedDetails = new RFIDCardDetails({});
    this.selectedDetails.active = true;
    this.workingCopyDetails = new RFIDCardDetails(this.selectedDetails);
    this.resetFormGroup();
  }

  clearAndLoad() {
    this.clear();
    this.load();
  }

  save() {
    if (this.rfidCards.some(s => {
      return (this.selectedRFIDCard == null && s.nbr === this.workingCopyDetails.nbr) || (this.selectedRFIDCard != null && s.id !== this.selectedRFIDCard.id && s.nbr === this.workingCopyDetails.nbr);
    }
    )) {
      const dialogData: ConfirmDialogData = {
        titleKey: 'tmc.admin.rfidcard.save-duplicate.confirm-dialog.title',
        messageKey: 'tmc.admin.rfidcard.save-duplicate.confirm-dialog.message',
        confirm: () => {
          this.saveWithoutCheck();
        }
      };
      this.dialog.open(ConfirmDialogComponent, {
        data: dialogData
      });
    } else {
      this.saveWithoutCheck();
    }
  }

  saveWithoutCheck() {
    this.setLoading(true);
    this.responseMessages = [];
    if (this.workingCopyDetails.id != null) {
      this.updateDetails(this.workingCopyDetails);
    } else {
      this.createDetails(this.workingCopyDetails);
    }
  }

  updateDetails(detailsToUpdate: RFIDCardDetails) {
    this.rfidCardService.saveRFIDCard(detailsToUpdate).subscribe(response => {
      this.setLoading(false);
      if (response.status === ResponseStatus.SUCCESS) {
        this.selectedDetails = new RFIDCardDetails(this.selectedDetails);
        this.resetFormGroup();
        this.load();
      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.setLoading(false);
    });
  }

  createDetails(detailsToAdd: RFIDCardDetails) {
    this.rfidCardService.createRFIDCard(detailsToAdd).subscribe(response => {
      this.setLoading(false);
      if (response.status === ResponseStatus.SUCCESS) {
        this.clearAndLoad();
      } else {
        this.responseMessages = response.responseMessages;
      }
    }, error => {
      console.log(error);
      this.setLoading(false);
    });
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
  }

  onSelectionChanged(entry: MasterDetailListEntry) {
    this.setSelected(entry as RFIDCard);
  }

  onAddEntry(parentPath: Array<MasterDetailListEntry>) {
    this.addNew();
  }

  onDeleteEntry(entry: MasterDetailListEntry) {
    this.delete(entry as RFIDCard);
  }

  private setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  private resetFormGroup() {
    const values: any = {};
    values.nbr = this.workingCopyDetails.nbr;
    values.techIdentifier = this.workingCopyDetails.techIdentifier;
    values.description = this.workingCopyDetails.description;
    values.active = this.workingCopyDetails.active;
    values.validFrom = this.workingCopyDetails.validFrom;
    values.validUntil = this.workingCopyDetails.validUntil;
    this.formGroup.reset(values);
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }

}
